import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useDebouncedAutoSave } from 'shared/hooks/useDebouncedAutoSave';
import { clearDraft, loadDraft, saveDraft } from 'shared/lib/storage';
import type { SupportedLanguage } from 'shared/i18n/i18n';
import { useLanguage } from 'shared/context/LanguageContext';
import type {
  BusinessInfo,
  BusinessQuestions,
  DeliveryCompany,
  Documents,
  FileMeta,
  OnboardingData,
  OnboardingMetadata,
  OnboardingSectionKey,
  OnboardingStatus,
  OpeningHours,
  DocumentKey,
} from 'features/onboarding/types/onboarding.types';
import { DELIVERY_COMPANIES } from 'features/onboarding/types/onboarding.types';

export const TOTAL_STEPS = 8;

/** Bumped when the linear step map changes so old drafts reset navigation. */
export const FLOW_VERSION = 2;

// The onboarding wizard opens directly on the business-info step. Step 1
// (`Step1Welcome`) is intentionally never reached — its code is kept but the
// intro screen is hidden.
export const FIRST_STEP = 2;

/**
 * The business step, which hosts the SMS gate inline: the partner reaches it
 * straight from the contact step but cannot move past it until the code sent to
 * their number is verified.
 */
export const VERIFICATION_STEP = FIRST_STEP + 1;

const defaultOpeningHours = (): OpeningHours => ({
  sunday: { enabled: true, mode: 'specific', intervals: [{ open: '09:00', close: '22:00' }] },
  monday: { enabled: true, mode: 'specific', intervals: [{ open: '09:00', close: '22:00' }] },
  tuesday: { enabled: true, mode: 'specific', intervals: [{ open: '09:00', close: '22:00' }] },
  wednesday: { enabled: true, mode: 'specific', intervals: [{ open: '09:00', close: '22:00' }] },
  thursday: { enabled: true, mode: 'specific', intervals: [{ open: '09:00', close: '22:00' }] },
  friday: { enabled: true, mode: 'specific', intervals: [{ open: '09:00', close: '15:00' }] },
  saturday: { enabled: true, mode: 'specific', intervals: [{ open: '09:00', close: '22:00' }] },
});

/**
 * Convert a legacy day shape `{ closed, open, close }` into the new
 * `{ enabled, mode, intervals }` shape. Tolerates partially-shaped objects
 * so we never throw on hydration.
 */
const migrateLegacyDay = (raw: unknown): OpeningHours[keyof OpeningHours] | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const looksNew =
    typeof r.enabled === 'boolean' &&
    (r.mode === 'specific' || r.mode === '24h') &&
    Array.isArray(r.intervals);
  if (looksNew) {
    return {
      enabled: r.enabled as boolean,
      mode: r.mode as 'specific' | '24h',
      intervals: (r.intervals as Array<Record<string, unknown>>)
        .filter((i) => i && typeof i.open === 'string' && typeof i.close === 'string')
        .map((i) => ({ open: i.open as string, close: i.close as string })),
    };
  }
  const looksLegacy =
    typeof r.closed === 'boolean' &&
    typeof r.open === 'string' &&
    typeof r.close === 'string';
  if (looksLegacy) {
    return {
      enabled: !(r.closed as boolean),
      mode: 'specific',
      intervals: [{ open: r.open as string, close: r.close as string }],
    };
  }
  return null;
};

const migrateOpeningHours = (raw: unknown): OpeningHours => {
  const fallback = defaultOpeningHours();
  if (!raw || typeof raw !== 'object') return fallback;
  const r = raw as Record<string, unknown>;
  const next: OpeningHours = { ...fallback };
  (Object.keys(fallback) as Array<keyof OpeningHours>).forEach((day) => {
    const migrated = migrateLegacyDay(r[day]);
    if (migrated) next[day] = migrated;
  });
  return next;
};

const emptyData = (language: SupportedLanguage = 'he'): OnboardingData => ({
  businessInfo: {
    commercialName: '',
    ownerName: '',
    phoneNumber: '',
    businessPhoneNumber: '',
    email: '',
    businessCategory: '',
    storeType: '',
    location: '',
    address: '',
    workingDays: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
    latitude: null,
    longitude: null,
  },
  businessQuestions: {
    deliveryCompanies: [],
    otherDeliveryCompanies: '',
    isKosher: null,
    kosherCertifications: [],
    sensitivities: [],
    storeTypeOther: '',
    menuSource: null,
    wantsMenuPhotoShoot: null,
  },
  taxLegalInfo: {
    taxId: '',
    officialBusinessName: '',
  },
  bankAccountInfo: {
    bankName: '',
    bankBranchNumber: '',
    bankAccountNumber: '',
    bankAccountOwnerName: '',
    bankAccountOwnerId: '',
  },
  representativeInfo: {
    representativeName: '',
    representativePhone: '',
    representativePosition: '',
  },
  finalDetails: {
    openingHours: defaultOpeningHours(),
    notes: '',
  },
  documents: {},
  metadata: {
    language,
    currentStep: FIRST_STEP,
    maxStepReached: FIRST_STEP,
    createdAt: new Date().toISOString(),
    status: 'Draft',
    phoneVerified: false,
    flowVersion: FLOW_VERSION,
  },
});

/**
 * The business step is gated by an SMS challenge: the wizard may not advance
 * past it until the number currently in the form is the one that was
 * verified. Editing the phone afterwards therefore re-locks the flow.
 */
export const isPhoneVerified = (data: OnboardingData): boolean =>
  data.metadata.phoneVerified === true &&
  !!data.metadata.verifiedPhone &&
  data.metadata.verifiedPhone === data.businessInfo.phoneNumber;

const hydrateDraft = (draft: OnboardingData): OnboardingData => {
  let next = draft;

  // Strip legacy fields removed from BusinessQuestions so old localStorage
  // drafts don't crash the validators.
  const bq = next.businessQuestions as BusinessQuestions & { needsMenuShoot?: unknown };
  if (bq && 'needsMenuShoot' in bq) {
    const { needsMenuShoot: _ignored, ...rest } = bq;
    next = { ...next, businessQuestions: rest as BusinessQuestions };
  }

  // Older drafts predate the structured deliveryCompanies multi-select.
  // If we see legacy free-text in otherDeliveryCompanies with no array, move
  // the text under the `'other'` sentinel so the user keeps what they typed.
  const bqDel = next.businessQuestions as BusinessQuestions & {
    deliveryCompanies?: unknown;
  };
  if (bqDel && !Array.isArray(bqDel.deliveryCompanies)) {
    const hasLegacyText = typeof bqDel.otherDeliveryCompanies === 'string'
      && bqDel.otherDeliveryCompanies.trim().length > 0;
    next = {
      ...next,
      businessQuestions: {
        ...bqDel,
        deliveryCompanies: hasLegacyText ? ['other'] : [],
      } as BusinessQuestions,
    };
  } else if (bqDel && Array.isArray(bqDel.deliveryCompanies)) {
    // Drop any persisted values that aren't in the current option set
    // (defensive against renamed/removed keys).
    const cleaned = (bqDel.deliveryCompanies as string[]).filter(
      (v): v is DeliveryCompany =>
        (DELIVERY_COMPANIES as readonly string[]).includes(v),
    );
    if (cleaned.length !== bqDel.deliveryCompanies.length) {
      next = {
        ...next,
        businessQuestions: { ...bqDel, deliveryCompanies: cleaned } as BusinessQuestions,
      };
    }
  }

  // Newer fields on BusinessQuestions: kosher certifications, menu source.
  // Default them so older drafts don't trip the validators. Legacy drafts
  // captured the menu via a yes/no `hasMenuFile` flag — map a previous "yes"
  // (with a stored file) onto the new `upload` source so the file is kept.
  if (next.businessQuestions) {
    const bqNew = next.businessQuestions as BusinessQuestions & {
      hasMenuFile?: boolean | null;
    };
    if (!Array.isArray(bqNew.kosherCertifications)) {
      next = {
        ...next,
        businessQuestions: { ...next.businessQuestions, kosherCertifications: [] },
      };
    }
    if (next.businessQuestions.menuSource === undefined) {
      const legacyMenuSource = bqNew.menuFile ? 'upload' : null;
      next = {
        ...next,
        businessQuestions: { ...next.businessQuestions, menuSource: legacyMenuSource },
      };
    }
    // Removed 'scratch' menu source — map any leftover drafts onto null so
    // the partner re-picks a supported option.
    if ((next.businessQuestions.menuSource as string | null) === 'scratch') {
      next = {
        ...next,
        businessQuestions: { ...next.businessQuestions, menuSource: null },
      };
    }
    // Newer `wantsMenuPhotoShoot` yes/no question — default older drafts to
    // null so the (required) validator prompts the user to answer it.
    if (next.businessQuestions.wantsMenuPhotoShoot === undefined) {
      next = {
        ...next,
        businessQuestions: { ...next.businessQuestions, wantsMenuPhotoShoot: null },
      };
    }
    if (!Array.isArray(next.businessQuestions.sensitivities)) {
      next = {
        ...next,
        businessQuestions: { ...next.businessQuestions, sensitivities: [] },
      };
    }
    if (next.businessQuestions.storeTypeOther === undefined) {
      next = {
        ...next,
        businessQuestions: { ...next.businessQuestions, storeTypeOther: '' },
      };
    }
  }

  // Legacy `businessType` (old 8-value enum) → new `storeType` (24-value list).
  const biLegacyType = next.businessInfo as BusinessInfo & {
    businessType?: string;
  };
  if (biLegacyType && 'businessType' in biLegacyType) {
    const map: Record<string, string> = {
      restaurant: 'Restaurant',
      cafe: 'Coffe Store',
      bakery: 'Bakery',
      grocery: 'Grocery',
      pharmacy: 'Pharmacy',
      butcher: 'Meat Butchery',
      dessert: 'Sweets',
      other: '(None)',
    };
    const { businessType, ...rest } = biLegacyType;
    next = {
      ...next,
      businessInfo: {
        ...(rest as BusinessInfo),
        storeType:
          (rest as BusinessInfo).storeType ||
          (businessType ? (map[businessType] as never) ?? '' : ''),
      },
    };
  }

  // Older drafts predate latitude/longitude on businessInfo — default to null.
  if (next.businessInfo) {
    const bi2 = next.businessInfo;
    if (bi2.latitude === undefined || bi2.longitude === undefined) {
      next = {
        ...next,
        businessInfo: {
          ...bi2,
          latitude: bi2.latitude ?? null,
          longitude: bi2.longitude ?? null,
        },
      };
    }
  }

  // Newer fields on businessInfo: businessCategory (Restaurant/Market) and
  // workingDays. Default them so older drafts don't trip the validators.
  if (next.businessInfo) {
    const biNew = next.businessInfo as BusinessInfo & {
      businessCategory?: unknown;
      workingDays?: unknown;
    };
    const patch: Partial<BusinessInfo> = {};
    if (
      biNew.businessCategory !== 'restaurant' &&
      biNew.businessCategory !== 'market'
    ) {
      patch.businessCategory = '';
    }
    if (!Array.isArray(biNew.workingDays)) {
      patch.workingDays = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];
    }
    if (Object.keys(patch).length > 0) {
      next = { ...next, businessInfo: { ...next.businessInfo, ...patch } };
    }
  }

  // Migration: `openingHours` used to live on `businessInfo` (first as a free
  // text string, later as a structured record). It now lives on
  // `finalDetails` together with the new `notes` field. Move any legacy value
  // across; reset string drafts to defaults.
  const bi = next.businessInfo as BusinessInfo & { openingHours?: unknown };
  const legacyOpeningHours = bi?.openingHours;
  if (bi && 'openingHours' in bi) {
    const { openingHours: _legacy, ...biClean } = bi as BusinessInfo & {
      openingHours?: unknown;
    };
    next = { ...next, businessInfo: biClean as BusinessInfo };
  }

  if (!next.finalDetails || typeof next.finalDetails !== 'object') {
    const recovered =
      legacyOpeningHours && typeof legacyOpeningHours === 'object'
        ? migrateOpeningHours(legacyOpeningHours)
        : defaultOpeningHours();
    next = {
      ...next,
      finalDetails: { openingHours: recovered, notes: '' },
    };
  } else if (!next.finalDetails.openingHours) {
    next = {
      ...next,
      finalDetails: {
        ...next.finalDetails,
        openingHours: defaultOpeningHours(),
      },
    };
  } else {
    // Migrate per-day legacy `{ closed, open, close }` shape if present.
    next = {
      ...next,
      finalDetails: {
        ...next.finalDetails,
        openingHours: migrateOpeningHours(next.finalDetails.openingHours),
      },
    };
  }

  // Drafts predating the SMS gate carry no verification flag.
  if (next.metadata && typeof next.metadata.phoneVerified !== 'boolean') {
    next = {
      ...next,
      metadata: { ...next.metadata, phoneVerified: false },
    };
  }

  // Older drafts predate `maxStepReached` — derive it from the saved
  // currentStep so previously visited steps remain unlocked after upgrade.
  if (next.metadata && typeof next.metadata.maxStepReached !== 'number') {
    next = {
      ...next,
      metadata: {
        ...next.metadata,
        maxStepReached: Math.max(next.metadata.currentStep ?? 1, 1),
      },
    };
  }

  // Legacy drafts predate the linear 9-step flow (they had an AI-consent
  // screen and a dynamic step order driven by `aiAssist`). Step indices from
  // that flow don't map cleanly onto the new one, so reset navigation to the
  // first step while preserving all entered data, and strip the dead field.
  const metaWithAi = next.metadata as OnboardingMetadata & {
    aiAssist?: 'accepted' | 'declined' | null;
  };
  if (metaWithAi && metaWithAi.aiAssist !== undefined) {
    const { aiAssist: _dropped, ...restMeta } = metaWithAi;
    next = {
      ...next,
      metadata: { ...(restMeta as OnboardingMetadata), currentStep: 1, maxStepReached: 1 },
    };
  }

  // Flow v2 merges the tax + bank steps into a single payment step and shifts
  // later indices down by one. Reset navigation for drafts that predate it
  // while keeping all entered data.
  if (
    next.metadata &&
    next.metadata.status !== 'Submitted' &&
    (next.metadata.flowVersion ?? 1) < FLOW_VERSION
  ) {
    next = {
      ...next,
      metadata: {
        ...next.metadata,
        flowVersion: FLOW_VERSION,
        currentStep: FIRST_STEP,
        maxStepReached: FIRST_STEP,
      },
    };
  } else if (next.metadata && next.metadata.flowVersion === undefined) {
    next = {
      ...next,
      metadata: { ...next.metadata, flowVersion: FLOW_VERSION },
    };
  }

  // Legacy docs were stored as base64 `dataUrl`. They now require a remote
  // `url`, so drop any old entries (the user will re-upload — much safer
  // than carrying tens of MB of base64 in state).
  if (next.documents && typeof next.documents === 'object') {
    const validDocKeys: DocumentKey[] = [
      'osekCertificate',
      'taxClearance',
      'bankConfirmation',
      'businessLogo',
    ];
    const cleaned: Documents = {};
    (Object.keys(next.documents) as DocumentKey[]).forEach((key) => {
      // Drop keys removed from the flow (e.g. legacy `ownerIdCard`).
      if (!validDocKeys.includes(key)) return;
      const file = next.documents[key] as (FileMeta & { dataUrl?: string }) | undefined;
      if (file && typeof file.url === 'string' && file.url.length > 0) {
        cleaned[key] = {
          name: file.name,
          type: file.type,
          size: file.size,
          url: file.url,
          uploadedAt: file.uploadedAt,
        };
      }
    });
    next = { ...next, documents: cleaned };
  }

  // The intro step (1) is hidden, so lift any draft parked there up to the
  // first visible step. Completed drafts on later steps (e.g. success) are
  // preserved by the Math.max.
  if (next.metadata) {
    next = {
      ...next,
      metadata: {
        ...next.metadata,
        currentStep: Math.max(next.metadata.currentStep ?? FIRST_STEP, FIRST_STEP),
        maxStepReached: Math.max(
          next.metadata.maxStepReached ?? FIRST_STEP,
          FIRST_STEP,
        ),
      },
    };
  }

  // A draft that has not cleared the SMS gate cannot resume past the step that
  // hosts it, but it may legitimately rest _on_ that step — so clamp instead of
  // sending the partner back to the contact step. Already-submitted drafts are
  // exempt: they predate the gate but have nothing left to fill.
  if (
    next.metadata &&
    next.metadata.status !== 'Submitted' &&
    !isPhoneVerified(next)
  ) {
    next = {
      ...next,
      metadata: {
        ...next.metadata,
        currentStep: Math.min(next.metadata.currentStep, VERIFICATION_STEP),
        maxStepReached: Math.min(next.metadata.maxStepReached, VERIFICATION_STEP),
      },
    };
  }

  return next;
};

/**
 * A guard a step component can register so that any navigation _away_ from
 * the step (via the stepper, programmatic jumps, etc.) runs the same
 * validation the step's "Next" button would. Returning `false` aborts the
 * jump. Steps that don't register a guard are treated as always-pass.
 */
export type StepGuard = (targetStep: number) => boolean | Promise<boolean>;

interface OnboardingContextValue {
  data: OnboardingData;
  totalSteps: number;
  updateSection: <K extends OnboardingSectionKey>(
    key: K,
    partial: Partial<OnboardingData[K]>,
  ) => void;
  setDocument: (key: DocumentKey, file: FileMeta | null) => void;
  setStep: (step: number) => Promise<boolean>;
  goNext: () => void;
  goBack: () => void;
  setMetadata: (partial: Partial<OnboardingMetadata>) => void;
  setStatus: (status: OnboardingStatus) => void;
  registerStepGuard: (guard: StepGuard | null) => void;
  reset: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { language } = useLanguage();
  const [data, setData] = useState<OnboardingData>(() => {
    const draft = loadDraft<OnboardingData>();
    const initial = draft ? hydrateDraft(draft) : emptyData(language);
    return {
      ...initial,
      metadata: { ...initial.metadata, language },
    };
  });

  useDebouncedAutoSave(data, saveDraft);

  useEffect(() => {
    setData((prev) =>
      prev.metadata.language === language
        ? prev
        : {
            ...prev,
            metadata: { ...prev.metadata, language },
          },
    );
  }, [language]);

  // The currently-mounted step can register a guard so jumps via the stepper
  // run the same validation as its "Next" button. Stored in a ref so updating
  // it doesn't trigger context re-renders or invalidate `setStep`.
  const stepGuardRef = useRef<StepGuard | null>(null);
  const registerStepGuard = useCallback((guard: StepGuard | null) => {
    stepGuardRef.current = guard;
  }, []);

  const updateSection = useCallback(
    <K extends OnboardingSectionKey>(key: K, partial: Partial<OnboardingData[K]>) => {
      setData((prev) => ({
        ...prev,
        [key]: { ...prev[key], ...partial },
      }));
    },
    [],
  );

  const setDocument = useCallback((key: DocumentKey, file: FileMeta | null) => {
    setData((prev) => {
      const documents: Documents = { ...prev.documents };
      if (file) {
        documents[key] = file;
      } else {
        delete documents[key];
      }
      return { ...prev, documents };
    });
  }, []);

  // Mirror of the latest `data` so async logic in setStep can read the
  // current step without relying on stale closures or scheduling extra
  // re-renders.
  const dataRef = useRef(data);
  dataRef.current = data;

  const setStep = useCallback(async (step: number): Promise<boolean> => {
    const current = dataRef.current.metadata.currentStep;
    const maxReached = dataRef.current.metadata.maxStepReached;
    const clamped = Math.min(Math.max(step, FIRST_STEP), TOTAL_STEPS);
    // Forward jumps are gated by maxStepReached so users can't skip past
    // unfilled sections via the stepper.
    const allowed = Math.min(clamped, maxReached);
    if (allowed === current) return true;
    if (allowed > VERIFICATION_STEP && !isPhoneVerified(dataRef.current)) {
      return false;
    }

    // Only validate when leaving a step _forward_. Backward jumps are
    // always free (matches how the rest of the flow behaves).
    if (allowed > current && stepGuardRef.current) {
      const ok = await stepGuardRef.current(allowed);
      if (!ok) return false;
    }

    setData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, currentStep: allowed },
    }));
    return true;
  }, []);

  const goNext = useCallback(() => {
    setData((prev) => {
      if (prev.metadata.currentStep === VERIFICATION_STEP && !isPhoneVerified(prev)) {
        return prev;
      }
      const next = Math.min(prev.metadata.currentStep + 1, TOTAL_STEPS);
      return {
        ...prev,
        metadata: {
          ...prev.metadata,
          currentStep: next,
          maxStepReached: Math.max(prev.metadata.maxStepReached, next),
        },
      };
    });
  }, []);

  const goBack = useCallback(() => {
    setData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        currentStep: Math.max(prev.metadata.currentStep - 1, FIRST_STEP),
      },
    }));
  }, []);

  const setMetadata = useCallback((partial: Partial<OnboardingMetadata>) => {
    setData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, ...partial },
    }));
  }, []);

  const setStatus = useCallback(
    (status: OnboardingStatus) => {
      setMetadata({ status });
    },
    [setMetadata],
  );

  const reset = useCallback(() => {
    clearDraft();
    setData(emptyData(language));
  }, [language]);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      data,
      totalSteps: TOTAL_STEPS,
      updateSection,
      setDocument,
      setStep,
      goNext,
      goBack,
      setMetadata,
      setStatus,
      registerStepGuard,
      reset,
    }),
    [
      data,
      updateSection,
      setDocument,
      setStep,
      goNext,
      goBack,
      setMetadata,
      setStatus,
      registerStepGuard,
      reset,
    ],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = (): OnboardingContextValue => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used inside OnboardingProvider');
  }
  return ctx;
};
